import { Component, type ErrorInfo, type ReactNode } from 'react'
import i18n from 'i18next'
import { logger } from '../services/LoggerService'

interface ErrorBoundaryProps {
  children: ReactNode
  fallback?: ReactNode
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    logger.error('React ErrorBoundary caught an error', {
      Component: 'ErrorBoundary',
      ErrorName: error.name,
      ComponentStack: errorInfo.componentStack || '',
    }, error)
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null })
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      return (
        <div className="flex items-center justify-center h-64 p-6">
          <div className="text-center max-w-md">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              {i18n.t('errors.somethingWentWrong', 'Something went wrong')}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              {i18n.t('errors.unexpectedError', 'An unexpected error occurred.')}
            </p>
            <button
              onClick={this.handleReset}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
            >
              {i18n.t('errors.tryAgain', 'Try again')}
            </button>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}
